// <t-custom-pack> web component for the custom-pack builder. Loaded
// globally (theme.liquid) so the class is registered before any instance
// hits the DOM — required for PDPs swapped in via DOMParser + appendChild
// (e.g. menu-with-pdp), where inline <script> tags don't execute.
if (!customElements.get('t-custom-pack')) {
  class TCustomPack extends HTMLElement {
    constructor() {
      super()
    }

    connectedCallback() {
      this.overlay = this.querySelector('[data-fs-overlay]')
      this.slotsGrid = this.querySelector('[data-slots-grid]')
      this.countDisplay = this.querySelector('[data-slot-count-display]')
      this.confirmBtn = this.querySelector('[data-fs-confirm]')
      this.confirmText = this.querySelector('[data-fs-confirm-text]')
      this.applyAllBtn = this.querySelector('[data-fs-apply-all]')
      this.applyAllText = this.querySelector('[data-fs-apply-all-text]')
      this.actionsEl = this.querySelector('[data-fs-actions]')
      this.applyConfirmEl = this.querySelector('[data-fs-apply-confirm]')
      this.applyConfirmText = this.querySelector('[data-fs-apply-confirm-text]')
      this.shirtIconHtml = this.querySelector('[data-shirt-icon]')?.innerHTML || ''
      this.pencilIconHtml = this.querySelector('[data-pencil-icon]')?.innerHTML || ''
      this.slotCount = Number(this.dataset.slotCount) || 6
      this.activeSlotIndex = null
      this.productInfo = this.closest('product-info')
      this.slotStyles = []

      this.boundHandleClick = this.handleClick.bind(this)
      this.boundHandleChange = this.handleChange.bind(this)
      this.boundHandleCartAdd = this.handleCartAddSuccess.bind(this)
      // a11y: Escape closes the sheet; focus is restored to the opener on close.
      this.boundHandleSheetKeydown = this.handleSheetKeydown.bind(this)

      this.addEventListener('click', this.boundHandleClick)
      this.addEventListener('change', this.boundHandleChange)
      document.addEventListener('cartaddsuccess', this.boundHandleCartAdd)

      // Capture-phase listeners decouple modal selectors from the page.
      // Without them, modal changes bubble to <variant-selects>/<product-
      // colors>, publish PDP events (optionValueSelectionChange /
      // onPackColorsChange), trigger a section re-render + URL push.
      // Bundle mode is entered once in openSheet; modal state stays local.
      this.boundHandleModalVariantChange = this.handleModalVariantChange.bind(this)
      this.boundHandleModalSwatchChange = this.handleModalSwatchChange.bind(this)
      const modalVariantSelects = this.querySelector('variant-selects')
      if (modalVariantSelects) {
        modalVariantSelects.addEventListener('change', this.boundHandleModalVariantChange, true)
      }
      const modalProductColors = this.querySelector('product-colors')
      if (modalProductColors) {
        modalProductColors.addEventListener('change', this.boundHandleModalSwatchChange, true)
      }

      this.renderSlots()
      this.syncConfirmState()
      this.observeHeaderCta()
    }

    // Replays the bounce on the "click/tap to customize" cta on every
    // viewport entry. Class is removed-then-readded with a forced reflow
    // so the keyframe restarts each time.
    observeHeaderCta() {
      this.headerCta = this.querySelector('[data-tcp-header-cta]')
      if (!this.headerCta || typeof IntersectionObserver === 'undefined') return

      this.headerCtaObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              entry.target.classList.remove('is-visible')
              return
            }
            entry.target.classList.remove('is-visible')
            void entry.target.offsetWidth
            entry.target.classList.add('is-visible')
          })
        },
        { threshold: 0.5 },
      )
      this.headerCtaObserver.observe(this.headerCta)
    }

    // Forced reflow so the keyframe replays on every call (incl. rapid
    // back-to-back clicks).
    shakeSlotsGrid() {
      if (!this.slotsGrid) return
      this.slotsGrid.classList.remove('is-shaking')
      void this.slotsGrid.offsetWidth
      this.slotsGrid.classList.add('is-shaking')
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.boundHandleClick)
      this.removeEventListener('change', this.boundHandleChange)
      document.removeEventListener('cartaddsuccess', this.boundHandleCartAdd)
      // Removed while open (e.g. host modal swapped content): release listeners + scroll lock.
      document.removeEventListener('keydown', this.boundHandleSheetKeydown)
      this.unlockBodyScroll()
      if (this.headerCtaObserver) {
        this.headerCtaObserver.disconnect()
        this.headerCtaObserver = null
      }
      const modalVariantSelects = this.querySelector('variant-selects')
      if (modalVariantSelects && this.boundHandleModalVariantChange) {
        modalVariantSelects.removeEventListener(
          'change',
          this.boundHandleModalVariantChange,
          true,
        )
      }
      const modalProductColors = this.querySelector('product-colors')
      if (modalProductColors && this.boundHandleModalSwatchChange) {
        modalProductColors.removeEventListener('change', this.boundHandleModalSwatchChange, true)
      }
    }

    handleModalVariantChange(event) {
      event.stopImmediatePropagation()
      // Size change → re-evaluate swatch availability against the new size.
      this.refreshModalSwatchAvailability()
      queueMicrotask(() => this.syncConfirmState())
    }

    handleModalSwatchChange(event) {
      event.stopImmediatePropagation()
      // Swatch change → re-evaluate size availability against the new
      // single. Swatch OOS state is keyed off size, not swatch — unchanged.
      this.refreshModalSizeAvailability()
      this.refreshModalGallery()
      this.refreshModalColorDisplay()
      queueMicrotask(() => this.syncConfirmState())
    }

    // Mirrors the main "Color: <name>" header inside the modal. Reads
    // the checked swatch's data-color-list, falls back to label/aria-label.
    refreshModalColorDisplay() {
      const display = this.querySelector('[data-tcp-modal-color-display]')
      if (!display) return
      const swatchInput = this.getModalSelectedSwatchInput()
      if (!swatchInput) {
        display.textContent = ''
        return
      }
      const label = swatchInput.closest('label')
      const color =
        swatchInput.dataset.colorList ||
        label?.getAttribute('aria-label') ||
        swatchInput.getAttribute('aria-label') ||
        ''
      display.textContent = color
    }

    // Toggle `.disabled` on each modal size radio against the currently
    // checked swatch's variants — mirrors server-rendered behavior of
    // the main variant-selects.
    refreshModalSizeAvailability() {
      const swatchInput = this.getModalSelectedSwatchInput()
      if (!swatchInput) return
      const handle = swatchInput.value
      const single = this.productInfo?.jsonData?.singles_data_json?.find(
        (p) => p.handle === handle,
      )
      if (!single) return

      const vs = this.getModalVariantSelects()
      if (!vs) return

      vs.querySelectorAll('input[type="radio"]').forEach((input) => {
        const variant = single.variants?.find((v) => v.title === input.value)
        const available = this.isVariantPurchasable(variant)
        input.classList.toggle('disabled', !available)
      })
    }

    // Toggle `.disabled` on each modal swatch based on whether that
    // single has the modal's currently-checked size available.
    refreshModalSwatchAvailability() {
      const size = this.getModalSelectedSize()
      if (!size) return
      const singles = this.productInfo?.jsonData?.singles_data_json
      if (!Array.isArray(singles)) return

      this.querySelectorAll('.liquid-pdp__product-color-swatch input').forEach((input) => {
        const handle = input.value
        const single = singles.find((p) => p.handle === handle)
        if (!single) return
        const variant = single.variants?.find((v) => v.title === size)
        const available = this.isVariantPurchasable(variant)
        input.classList.toggle('disabled', !available)
        input.dataset.disabled = available ? 'false' : 'true'
        this.productInfo.setSwatchFontanaOosState?.(input, variant)
      })

      this.sortModalSwatchesByAvailability()
    }

    sortModalSwatchesByAvailability() {
      this.querySelectorAll('.liquid-pdp__product-color-swatches-wrapper').forEach((wrapper) => {
        const labels = Array.from(wrapper.querySelectorAll('.liquid-pdp__product-color-swatch'))
        if (!labels.length) return

        const swatchLimit = Number(wrapper.dataset.swatchLimit) || 9
        labels.sort((a, b) => {
          const aDisabled = a.querySelector('input')?.classList.contains('disabled')
          const bDisabled = b.querySelector('input')?.classList.contains('disabled')
          return Number(aDisabled) - Number(bDisabled)
        })

        labels.forEach((label, index) => {
          label.classList.toggle('liquid-pdp__product-color-swatch--hidden', index >= swatchLimit)
          wrapper.appendChild(label)
        })

        const toggle = wrapper.querySelector('.liquid-pdp__singles-swatches__toggle')
        if (!toggle) return

        wrapper.appendChild(toggle)
        this.syncModalSwatchToggle(wrapper, labels, toggle)
      })
    }

    syncModalSwatchToggle(wrapper, labels, toggle) {
      const hiddenSelectable = labels.some((label) => {
        const input = label.querySelector('input')
        return (
          label.classList.contains('liquid-pdp__product-color-swatch--hidden') &&
          !input?.classList.contains('disabled')
        )
      })

      if (!hiddenSelectable) {
        toggle.classList.add('hide')
        wrapper.classList.remove('showing-all-swatches')
        const showMoreText = toggle.getAttribute('data-show-more-text')
        if (showMoreText) toggle.textContent = showMoreText
        return
      }

      toggle.classList.remove('hide')
      const text = wrapper.classList.contains('showing-all-swatches')
        ? toggle.getAttribute('data-show-less-text')
        : toggle.getAttribute('data-show-more-text')
      if (text) toggle.textContent = text
    }

    handleClick(event) {
      // "click/tap to customize" cta — shakes the slots to draw the eye.
      if (event.target.closest('[data-tcp-header-cta]')) {
        this.shakeSlotsGrid()
        return
      }

      // Dead branch — remove button markup is commented out in
      // renderFilledSlot, kept for easy re-enable.
      if (event.target.closest('[data-slot-remove]')) {
        event.stopPropagation()
        const filled = event.target.closest('[data-slot-filled]')
        if (filled) this.removeSlot(Number(filled.dataset.slotIndex))
        return
      }

      // Filled tile click = edit. Checked before empty-slot branch since
      // both render as <button>.
      const filledSlot = event.target.closest('[data-slot-filled]')
      if (filledSlot) {
        this.openSheet(Number(filledSlot.dataset.slotIndex), 'edit')
        return
      }

      const emptySlot = event.target.closest('[data-slot-empty]')
      if (emptySlot) {
        this.openSheet(Number(emptySlot.dataset.slotIndex), 'add')
        return
      }

      if (event.target.closest('[data-fs-confirm]')) {
        this.confirmSelection()
        return
      }

      if (event.target.closest('[data-fs-apply-all]')) {
        this.showApplyAllConfirm()
        return
      }

      if (event.target.closest('[data-fs-apply-cancel]')) {
        this.hideApplyAllConfirm()
        this.applyAllBtn?.focus({ preventScroll: true })
        return
      }

      if (event.target.closest('[data-fs-apply-ok]')) {
        this.applySelectionToAllSlots()
        return
      }

      if (event.target.closest('[data-fs-close]')) {
        this.closeSheet()
        return
      }

      if (event.target === this.overlay) this.closeSheet()
    }

    handleChange() {
      // PDP code updates the main submit button async (pubsub + form
      // state refresh). Defer so we read the latest state.
      queueMicrotask(() => this.syncConfirmState())
    }

    handleCartAddSuccess() {
      if (this.getBundleSelections().length === 0) {
        this.slotStyles = []
        this.renderSlots()
      }
    }

    getProductForm() {
      return this.productInfo?.querySelector('product-form') || null
    }

    getMainSubmitButton() {
      return this.productInfo?.querySelector('product-form form button[type="submit"]') || null
    }

    getBundleSelections() {
      if (!this.productInfo) return []
      if (!Array.isArray(this.productInfo.bundleSelections)) {
        this.productInfo.bundleSelections = []
      }
      return this.productInfo.bundleSelections
    }

    // Modal-local. Decoupled from the main selector — variant resolves
    // from modal size + swatch, not form input[name="id"].
    getModalVariantSelects() {
      return this.querySelector('variant-selects')
    }

    getModalSelectedSize() {
      const vs = this.getModalVariantSelects()
      if (!vs) return ''
      return Array.from(vs.querySelectorAll('input:checked'))
        .map((input) => input.value)
        .join(' / ')
    }

    getModalSelectedSwatchInput() {
      return this.querySelector('.liquid-pdp__product-color-swatch input:checked') || null
    }

    getModalGallery() {
      return this.querySelector('product-gallery')
    }

    // Sync modal gallery to the checked swatch's single. Silent bail
    // when swiper isn't initialized yet or jsonData is missing.
    refreshModalGallery() {
      const gallery = this.getModalGallery()
      if (!gallery || typeof gallery.updateGalleryMedia !== 'function') return
      if (!gallery.swiper) {
        // product-gallery delays swiper init 500ms; retry next frame.
        requestAnimationFrame(() => this.refreshModalGallery())
        return
      }

      const swatchInput = this.getModalSelectedSwatchInput()
      if (!swatchInput) return
      const handle = swatchInput.value
      const single = this.productInfo?.jsonData?.singles_data_json?.find(
        (p) => p.handle === handle,
      )
      if (!single || !Array.isArray(single.media) || !single.media.length) return

      gallery.updateGalleryMedia(
        single.media,
        single.carousel_video || null,
        this.productInfo?.jsonData?.video_data || null,
        null,
      )
    }

    // Main = page's primary <variant-selects> — skips t-custom-pack
    // copies and viewTransition's display:none leftover.
    getMainSelectedSize() {
      const main = Array.from(document.querySelectorAll('variant-selects')).find(
        (el) => !el.closest('t-custom-pack') && el.style.display !== 'none',
      )
      if (!main) return ''
      return Array.from(main.querySelectorAll('input:checked'))
        .map((input) => input.value)
        .join(' / ')
    }

    // Reads the main PDP's selected size (joined for multi-option products
    // like "M / Tall"). Mirrors the MAIN-variant-selects guard used in
    // liquid-pdp.js preFillBundleFromPack — productInfo.selectedSize would
    // join the modal's mirror radios too, yielding "M / M".
    getMainSelectedSize() {
      if (!this.productInfo) return ''
      const mainVariantSelects = Array.from(
        this.productInfo.querySelectorAll('variant-selects'),
      ).find((el) => !el.closest('t-custom-pack') && el.style.display !== 'none')
      if (!mainVariantSelects) return ''
      return Array.from(mainVariantSelects.querySelectorAll('input:checked'))
        .map((input) => input.value)
        .join(' / ')
    }

    setModalCheckedSize(size) {
      const vs = this.getModalVariantSelects()
      if (!vs || !size) return false
      const radios = Array.from(vs.querySelectorAll('input[type="radio"]'))
      let matched = false
      radios.forEach((input) => {
        const isMatch = input.value === size
        input.checked = isMatch
        if (isMatch) matched = true
      })
      return matched
    }

    // Wipe modal's checked size — fresh open of a sizeless slot must
    // not inherit state from a prior open.
    clearModalCheckedSize() {
      const vs = this.getModalVariantSelects()
      if (!vs) return
      vs.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
        input.checked = false
      })
    }

    // Resolves the modal's selection (size + swatch handle) to a singles
    // variant via singles_data_json. Null when anything is missing or
    // the resulting variant is unavailable.
    buildSelectionFromModal() {
      if (!this.productInfo) return null

      const swatchInput = this.getModalSelectedSwatchInput()
      if (!swatchInput) return null

      const handle = swatchInput.value
      const singles = this.productInfo.jsonData?.singles_data_json
      const single = Array.isArray(singles) ? singles.find((p) => p.handle === handle) : null
      if (!single) return null

      const size = this.getModalSelectedSize()
      if (!size) return null

      const variant = single.variants?.find((v) => v.title === size)
      if (!variant || !this.isVariantPurchasable(variant)) return null

      const colorList = swatchInput.dataset.colorList
      const color =
        (colorList && colorList.split(',')[0].trim()) || single.title || 'Selected color'
      return { id: variant.id, title: variant.title, color, handle }
    }

    // In stock and (outside the US) not Fontana-OOS.
    isVariantPurchasable(variant) {
      return this.productInfo?.isSingleVariantPurchasable?.(variant) ?? variant?.available === true
    }

    isVariantReady() {
      return this.buildSelectionFromModal() !== null
    }

    syncConfirmState() {
      if (!this.confirmBtn) return
      const ready = this.isVariantReady()
      const readyLabel = this.activeMode === 'edit' ? 'Update (1)' : 'Add to Bundle (1)'

      if (ready) {
        this.confirmBtn.disabled = false
        this.confirmBtn.setAttribute('aria-disabled', 'false')
        if (this.confirmText) this.confirmText.textContent = readyLabel
      } else {
        this.confirmBtn.disabled = true
        this.confirmBtn.setAttribute('aria-disabled', 'true')
        if (this.confirmText) {
          const needsColor = this.getModalSelectedSize() && !this.getModalSelectedSwatchInput()
          this.confirmText.textContent = needsColor ? 'Select a color' : 'Select a size'
        }
      }

      this.syncApplyAllState(ready)
    }

    // Every slot of the pack, matching the cap appendSelection enforces.
    getTotalSlotCount() {
      return this.productInfo?.bundleLimit || this.slotCount
    }

    syncApplyAllState(ready) {
      if (!this.applyAllBtn) return
      this.applyAllBtn.disabled = !ready
      this.applyAllBtn.setAttribute('aria-disabled', ready ? 'false' : 'true')
      if (this.applyAllText) {
        this.applyAllText.textContent = `Apply To All (${this.getTotalSlotCount()})`
      }

      // A swatch/size change while the question is open re-words it, or
      // drops it when the selection is no longer valid.
      if (this.applyConfirmEl && !this.applyConfirmEl.hidden) {
        if (ready) this.refreshApplyAllConfirmText()
        else this.hideApplyAllConfirm()
      }
    }

    refreshApplyAllConfirmText() {
      const selection = this.buildSelectionFromModal()
      if (!selection || !this.applyConfirmText) return

      const label = document.createElement('strong')
      label.textContent = `${selection.color} · ${selection.title}`
      this.applyConfirmText.replaceChildren(
        'Apply ',
        label,
        ` to all ${this.getTotalSlotCount()} shirts in your pack?`,
      )
    }

    showApplyAllConfirm() {
      if (!this.applyConfirmEl || !this.actionsEl || !this.isVariantReady()) return
      this.refreshApplyAllConfirmText()
      this.actionsEl.hidden = true
      this.applyConfirmEl.hidden = false
      this.applyConfirmEl.querySelector('[data-fs-apply-cancel]')?.focus({ preventScroll: true })
    }

    hideApplyAllConfirm() {
      if (!this.applyConfirmEl || !this.actionsEl) return
      this.applyConfirmEl.hidden = true
      this.actionsEl.hidden = false
    }

    // Fills every slot of the pack with the modal's current selection.
    applySelectionToAllSlots() {
      if (!this.productInfo) return

      const selection = this.buildSelectionFromModal()
      if (!selection) return

      if (this.productInfo.dataset.bundleMode !== 'true') {
        if (typeof this.productInfo.enterBundleMode === 'function') {
          this.productInfo.enterBundleMode()
        } else if (typeof this.productInfo.setBundleMode === 'function') {
          this.productInfo.setBundleMode(true)
        }
      }

      const swatchBg = this.getSelectedSwatchBackground()
      const total = this.getTotalSlotCount()
      this.productInfo.bundleSelections = Array.from({ length: total }, () => ({ ...selection }))
      this.slotStyles = Array.from({ length: total }, () => swatchBg)

      if (typeof this.productInfo.updateBundleModeUi === 'function') {
        this.productInfo.updateBundleModeUi()
      }
      this.renderSlots()
      this.closeSheet()
    }

    confirmSelection() {
      if (!this.productInfo) return

      const selection = this.buildSelectionFromModal()
      if (!selection) return

      // First slot edit promotes the page into bundle mode (gallery/
      // title/price swap to the custom-pack, tiles deselect, heading ->
      // "Custom Colors").
      if (this.productInfo.dataset.bundleMode !== 'true') {
        if (typeof this.productInfo.enterBundleMode === 'function') {
          this.productInfo.enterBundleMode()
        } else if (typeof this.productInfo.setBundleMode === 'function') {
          this.productInfo.setBundleMode(true)
        }
      }

      const swatchBg = this.getSelectedSwatchBackground()

      if (this.activeMode === 'edit' && typeof this.activeSlotIndex === 'number') {
        this.replaceSelectionAt(this.activeSlotIndex, selection, swatchBg)
      } else {
        this.appendSelection(selection, swatchBg)
      }

      this.closeSheet()
    }

    appendSelection(selection, swatchBg) {
      const selections = this.getBundleSelections()
      const bundleLimit = this.productInfo.bundleLimit || this.slotCount
      if (selections.length >= bundleLimit) return

      selections.push(selection)
      this.slotStyles.push(swatchBg)
      this.productInfo.bundleSelections = selections

      if (typeof this.productInfo.updateBundleModeUi === 'function') {
        this.productInfo.updateBundleModeUi()
      }
      this.renderSlots()
    }

    // Bulk-update every slot's variant to the given size. Main size
    // selector overwrites any per-slot sizes picked in the modal.
    applyMainSizeToAllSlots(size) {
      if (!size) return
      const selections = this.getBundleSelections()
      if (!selections.length) return

      const singles = this.productInfo?.jsonData?.singles_data_json
      if (!Array.isArray(singles)) return

      let updatedCount = 0
      const updated = selections.map((selection) => {
        const single =
          (selection.handle && singles.find((p) => p.handle === selection.handle)) ||
          singles.find((p) => p.variants?.some((v) => v.id === selection.id))
        if (!single) return selection
        const variant = single.variants.find((v) => v.title === size)
        if (!variant) {
          // Single doesn't carry this size (rare). Mark the slot as
          // needing re-selection — picks up unavailable styling + blocks
          // ATC.
          if (selection.id == null && selection.title === size) return selection
          updatedCount += 1
          return {
            id: null,
            title: size,
            color: selection.color,
            handle: selection.handle || single.handle,
          }
        }
        if (variant.id === selection.id) return selection
        updatedCount += 1
        return {
          id: variant.id,
          title: variant.title,
          color: selection.color,
          handle: selection.handle || single.handle,
        }
      })

      if (!updatedCount) return

      this.productInfo.bundleSelections = updated
      if (typeof this.productInfo.updateBundleModeUi === 'function') {
        this.productInfo.updateBundleModeUi()
      }
      this.renderSlots()
    }

    replaceSelectionAt(index, selection, swatchBg) {
      const selections = this.getBundleSelections()
      if (index < 0 || index >= selections.length) return

      selections[index] = selection
      this.slotStyles[index] = swatchBg
      this.productInfo.bundleSelections = selections

      if (typeof this.productInfo.updateBundleModeUi === 'function') {
        this.productInfo.updateBundleModeUi()
      }
      this.renderSlots()
    }

    getSelectedSwatchBackground() {
      const checked = this.querySelector('.liquid-pdp__product-color-swatch input:checked')
      if (!checked) return ''
      const label = checked.closest('.liquid-pdp__product-color-swatch')
      const colorEl = label?.querySelector('.color')
      return colorEl?.style.background || colorEl?.style.backgroundColor || ''
    }

    removeSlot(index) {
      const selections = this.getBundleSelections()
      if (index < 0 || index >= selections.length) return

      selections.splice(index, 1)
      this.slotStyles.splice(index, 1)
      this.productInfo.bundleSelections = selections

      if (typeof this.productInfo.updateBundleModeUi === 'function') {
        this.productInfo.updateBundleModeUi()
      }
      this.renderSlots()
    }

    isSheetOpen() {
      return this.overlay?.dataset.open === 'true'
    }

    // A size chart opened over the sheet owns Escape until it closes.
    hasOpenSizeChart() {
      return Boolean(document.querySelector('size-chart-modal.is-open, size-chart-modal-tiles.is-open'))
    }

    // a11y: Escape closes the sheet while it's open (topmost modal only).
    handleSheetKeydown(event) {
      if (event.key !== 'Escape' || this.hasOpenSizeChart()) return
      this.closeSheet()
    }

    // Hosts like the quick-add modal already lock body scroll with an !important
    // inline overflow, so remember the prior value/priority and restore exactly that
    // on unlock (clearing it would unlock the page under the still-open host).
    lockBodyScroll() {
      if (this.previousBodyOverflow) return
      const bodyStyle = document.body.style
      this.previousBodyOverflow = {
        value: bodyStyle.getPropertyValue('overflow'),
        priority: bodyStyle.getPropertyPriority('overflow'),
      }
      bodyStyle.setProperty('overflow', 'hidden', 'important')
    }

    unlockBodyScroll() {
      const previous = this.previousBodyOverflow
      if (!previous) return
      this.previousBodyOverflow = null
      const bodyStyle = document.body.style
      if (previous.value) bodyStyle.setProperty('overflow', previous.value, previous.priority)
      else bodyStyle.removeProperty('overflow')
    }

    openSheet(slotIndex, mode = 'add') {
      this.hideApplyAllConfirm()
      this.activeSlotIndex = slotIndex
      this.activeMode = mode
      // Remember the trigger so focus can return to it on close.
      this.sheetOpener = document.activeElement
      this.overlay.dataset.open = 'true'
      this.overlay.setAttribute('aria-hidden', 'false')
      this.overlay.removeAttribute('inert')
      this.lockBodyScroll()
      document.addEventListener('keydown', this.boundHandleSheetKeydown)

      // Opening the modal enters bundle mode (same as slot confirm).
      // Modal swatch/size are decoupled afterwards — user can browse
      // singles freely without affecting the main page.
      if (this.productInfo && this.productInfo.dataset.bundleMode !== 'true') {
        if (typeof this.productInfo.enterBundleMode === 'function') {
          this.productInfo.enterBundleMode()
        } else if (typeof this.productInfo.setBundleMode === 'function') {
          this.productInfo.setBundleMode(true)
        }
      }

      // Mirror slot size into the modal. Sizeless slots fall back to the
      // main PDP's currently-selected size so a user who's already picked
      // one doesn't have to re-pick inside every slot.
      const slotSelection = this.getBundleSelections()[slotIndex]
      const slotSize = slotSelection?.title || this.getMainSelectedSize()
      if (slotSize) {
        this.setModalCheckedSize(slotSize)
      } else {
        this.clearModalCheckedSize()
      }

      if (mode === 'edit') this.preselectModalSingleForSlot(slotIndex)

      this.refreshModalSizeAvailability()
      this.refreshModalSwatchAvailability()
      this.refreshModalGallery()
      this.refreshModalColorDisplay()
      this.syncConfirmState()

      // Move focus into the sheet after setup so keyboard/SR users land inside
      // the dialog. Deferred to the next frame so it happens once the sheet is
      // visible and no later setup step steals focus.
      requestAnimationFrame(() => {
        this.overlay.querySelector('[data-fs-close]')?.focus({ preventScroll: true })
      })
    }

    // Pre-checks the slot's single in the modal swatches. Without this
    // an immediate "Update" click resolves against whichever swatch is
    // currently checked (the page's active pack).
    preselectModalSingleForSlot(slotIndex) {
      const selection = this.getBundleSelections()[slotIndex]
      if (!selection) return

      // Pre-fill stamps `handle` on sizeless slots so swatches resolve
      // even when no variant id exists yet.
      const handle = selection.handle || this.findSingleHandleForVariantId(selection.id)
      if (!handle) return

      const swatchInput = this.querySelector(
        `.liquid-pdp__product-color-swatch input[value="${handle}"]`,
      )
      if (!swatchInput) return
      if (swatchInput.checked) return

      this.expandSwatchWrapperIfHidden(swatchInput)

      // Set silently — openSheet refreshes OOS state right after.
      swatchInput.checked = true
    }

    findSingleHandleForVariantId(variantId) {
      const singles = this.productInfo?.jsonData?.singles_data_json
      if (!Array.isArray(singles)) return null
      const match = singles.find((p) => p.variants?.some((v) => v.id === variantId))
      return match?.handle || null
    }

    expandSwatchWrapperIfHidden(swatchInput) {
      const label = swatchInput.closest('.liquid-pdp__product-color-swatch')
      if (!label?.classList.contains('liquid-pdp__product-color-swatch--hidden')) return

      const wrapper = label.closest('.liquid-pdp__product-color-swatches-wrapper')
      if (!wrapper || wrapper.classList.contains('showing-all-swatches')) return

      wrapper.classList.add('showing-all-swatches')
      const toggle = wrapper.querySelector('.liquid-pdp__singles-swatches__toggle')
      if (toggle) {
        const showLessText = toggle.getAttribute('data-show-less-text')
        if (showLessText) toggle.textContent = showLessText
      }
    }

    closeSheet() {
      // No-op when closed (hosts call this defensively when they close).
      if (!this.isSheetOpen()) return
      this.hideApplyAllConfirm()
      this.activeSlotIndex = null
      this.activeMode = null
      this.overlay.dataset.open = 'false'
      this.overlay.setAttribute('aria-hidden', 'true')
      this.overlay.setAttribute('inert', '')
      this.unlockBodyScroll()
      document.removeEventListener('keydown', this.boundHandleSheetKeydown)
      // Restore focus to whatever opened the sheet (if still in the DOM).
      if (this.sheetOpener && document.contains(this.sheetOpener)) {
        this.sheetOpener.focus({ preventScroll: true })
      }
      this.sheetOpener = null
    }

    renderSlots() {
      if (!this.slotsGrid) return
      const selections = this.getBundleSelections()
      const filled = selections.length

      if (this.countDisplay) {
        this.countDisplay.textContent = `${filled} / ${this.slotCount}`
      }

      let html = ''
      for (let i = 0; i < this.slotCount; i++) {
        if (i < filled) {
          html += this.renderFilledSlot(i, selections[i], this.slotStyles[i] || '')
        } else {
          html += this.renderEmptySlot(i)
        }
      }
      this.slotsGrid.innerHTML = html

      if (typeof this.productInfo?.syncMainSizeFromBundle === 'function') {
        this.productInfo.syncMainSizeFromBundle()
      }
      if (typeof this.productInfo?.updateBundleGalleryFromSelections === 'function') {
        this.productInfo.updateBundleGalleryFromSelections()
      }
    }

    renderFilledSlot(index, selection, bg) {
      const sizeRaw = selection?.title || ''
      const size = escapeHtml(sizeRaw)
      const color = escapeHtml(selection?.color || '')
      const imgUrl = this.getSlotImageUrl(selection)
      // Colored shirt SVG fallback when no image is resolvable (single
      // missing from singles_data_json, no media, etc.).
      const fill = resolveShirtFill(bg)
      const shirtSvg = this.shirtIconHtml.replaceAll('__TCP_FILL__', escapeAttr(fill))
      const mediaHtml = imgUrl
        ? `<img class="t-custom-pack__slot-image" src="${escapeAttr(
            imgUrl,
          )}" alt="${color}" loading="lazy" />`
        : `<span class="t-custom-pack__slot-shirt">${shirtSvg}</span>`
      const sizeSpan = sizeRaw ? `<span class="t-custom-pack__slot-size">${size}</span>` : ''
      // OOS styling (dimmed + strikethrough) mirrors pack tiles. Sizeless
      // slots skip it so they keep their "needs a size" look.
      const available = !sizeRaw || (this.productInfo?.isSelectionAvailable?.(selection) ?? true)
      // Fontana OOS (red X) wins over general OOS (white X).
      const isFontanaOos = this.productInfo?.isSelectionFontanaOos?.(selection) ?? false
      const isDimmed = !available || isFontanaOos
      const unavailableClass = isDimmed ? ' t-custom-pack__slot--unavailable' : ''
      const strikethroughModifier = isFontanaOos
        ? ' t-custom-pack__slot-strikethrough--fontana'
        : ''
      const strikethrough = isDimmed
        ? `<span class="t-custom-pack__slot-strikethrough${strikethroughModifier}" aria-hidden="true"></span>`
        : ''
      return `
        <button
          type="button"
          class="t-custom-pack__slot t-custom-pack__slot--filled${
            sizeRaw ? '' : ' t-custom-pack__slot--sizeless'
          }${imgUrl ? ' t-custom-pack__slot--has-image' : ''}${unavailableClass}"
          data-slot-filled
          data-slot-index="${index}"
          aria-label="Edit slot ${index + 1}"
        >
          ${mediaHtml}
          ${strikethrough}
          <!-- Remove button kept commented for easy re-enable.
            <div class="t-custom-pack__slot-actions">
              <button
                type="button"
                class="t-custom-pack__slot-action t-custom-pack__slot-action--remove"
                data-slot-remove
                aria-label="Remove slot ${index + 1}"
              >&#10005;</button>
            </div>
          -->
          <span class="t-custom-pack__slot-hover" aria-hidden="true">
            <span class="t-custom-pack__slot-hover-button">
              <span class="t-custom-pack__slot-hover-icon">${this.pencilIconHtml}</span>
            </span>
          </span>
          <div class="t-custom-pack__slot-text">
            <span class="t-custom-pack__slot-color">${color}</span>
            ${sizeSpan}
          </div>
        </button>`
    }

    // Slot tile image URL — second media item if it exists (better
    // visual than the featured shot), else first. '' when none.
    getSlotImageUrl(selection) {
      const handle = selection?.handle
      if (!handle) return ''
      const singles = this.productInfo?.jsonData?.singles_data_json
      if (!Array.isArray(singles)) return ''
      const single = singles.find((p) => p.handle === handle)
      const media = single?.media
      if (!Array.isArray(media) || !media.length) return ''
      const item = media[1] || media[0]
      const src = item?.src
      if (!src) return ''
      const sep = src.includes('?') ? '&' : '?'
      return `${src}${sep}width=400`
    }

    renderEmptySlot(index) {
      const slotNumber = index + 1
      return `
        <button
          type="button"
          class="t-custom-pack__slot t-custom-pack__slot--empty"
          data-slot-empty
          data-slot-index="${index}"
          aria-label="Add shirt to slot ${slotNumber}"
        >
          <span class="t-custom-pack__slot-plus">+</span>
        </button>`
    }
  }

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    })
  }

  function escapeAttr(str) {
    return String(str).replace(/"/g, '&quot;')
  }

  const WHITE_FALLBACK = '#d4d4d4'
  const DEFAULT_SHIRT_FILL = '#9a9088'

  // Normalize a CSS color to computed `rgb(a)` so we can detect whites/
  // near-whites regardless of how they were authored.
  function toComputedColor(input) {
    if (!input) return ''
    const probe = document.createElement('span')
    probe.style.color = 'transparent'
    probe.style.color = input
    if (!probe.style.color) return ''
    document.body.appendChild(probe)
    const computed = getComputedStyle(probe).color
    document.body.removeChild(probe)
    return computed
  }

  function isWhiteColor(input) {
    const computed = toComputedColor(input)
    const parts = computed.match(/\d+(\.\d+)?/g)
    if (!parts || parts.length < 3) return false
    const [r, g, b] = parts.map(Number)
    return r >= 245 && g >= 245 && b >= 245
  }

  function resolveShirtFill(swatchBg) {
    if (!swatchBg) return DEFAULT_SHIRT_FILL
    return isWhiteColor(swatchBg) ? WHITE_FALLBACK : swatchBg
  }

  customElements.define('t-custom-pack', TCustomPack)
}
